import {Injectable, OnDestroy} from '@angular/core';
import {Attachment} from '@docma-mm-core/attachment/attachment';
import {AttachmentType} from '@docma-mm-core/attachment/attachment-type';
import {AttachmentService} from '@docma-mm-core/attachment/attachment.service';
import {ImageViewerOptions, ImageViewerService, ViewerItem} from '@mydocma/component-library/src/lib/image-viewer';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, combineLatest, firstValueFrom, pairwise, Subscription} from 'rxjs';
import {delay, filter, take, tap} from 'rxjs/operators';
import {AnnotationService, AnnoTypeEnum} from '@mydocma/component-library/src/lib/annotation';
import {MmAnnotationService} from './mm-faults/mm-fault/mm-fault-attachments/mm-annotations/annotation.service';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {MmProjectLoaderService} from './mm-project/mm-project-loader.service';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {User} from '@docma-mm-core/user/user';
import {AnnotationPermissionsService} from './mm-faults/mm-fault/mm-fault-attachments/mm-annotations/annotation-permissions.service';
import {AnnotationViewService} from './mm-faults/mm-fault/mm-fault-attachments/mm-annotations/annotation-view.service';


@UntilDestroy()
@Injectable()
export class MmImageViewerService extends ImageViewerService<Attachment> implements OnDestroy {
  public annotationService: AnnotationService;
  private annoLayerReady$ = new BehaviorSubject<boolean>(false);
  private currentUser: User;
  private selectedAnnotationsSubscription: Subscription;

  public currentFault: ViewFault;
  public closing$ = new BehaviorSubject<boolean>(false);

  private startInAnnoEditMode = false;

  private customLoadingSubject = new BehaviorSubject<boolean>(false);
  public customLoading$ = this.customLoadingSubject.asObservable();

  public imageViewerOptions: ImageViewerOptions = {
    useCustomFullscreenForIpad: true,
    useLeafletContainer: false,
    annotations: {
      allowEdit: false,
      onLayerReady: async (params) => {
        this.annotationService = params.annotationService;
        this.annoLayerReady$.next(true);
        this.mmAnnotationService.attachTo(params.annotationService);

        this.selectedAnnotationsSubscription = this.annotationService.selectedAnnotations$
          .pipe(untilDestroyed(this))
          .subscribe(async (selectedAnnos) => {
            if (selectedAnnos.length === 0) return;

            const selectedAnnotation = selectedAnnos[0];

            const isEditableType = this.annotationPermissionsService.isEditableAnnotationType(selectedAnnotation.type);
            if (isEditableType) return;

            await this.annotationViewService.showFatClientWarning();
          });
        this.annotationService.editMode$.pipe(
          pairwise(),
          filter(([prev, next]) => prev === true && next === false),
          filter(() => this.startInAnnoEditMode),
          tap(() => this.closing$.next(true)),
          delay(40),
          // we need to restore once per session, just guard, not necessary
          // take(1),
          untilDestroyed(this)
        ).subscribe(() => {
          this.fullScreen = false;
          this.closing$.next(false);
          this.startInAnnoEditMode = false;
        });
      },
    },
    onImageLoad: async (e) => {
      this.mmAnnotationService.attachment = this.currentItem as Attachment;
      this.mmAnnotationService.currentFault = this.currentFault;
      await this.updateAnnotationPermissions();

      if (this.startInAnnoEditMode) {
        await this.waitForFullscreenAndAnno();
        await this.annotationService.startEdit();
      }

      this.customLoadingSubject.next(false);
    },
  };

  constructor(
    private readonly attachmentService: AttachmentService,
    private readonly mmAnnotationService: MmAnnotationService,
    private readonly projectLoaderService: MmProjectLoaderService,
    private readonly annotationPermissionsService: AnnotationPermissionsService,
    private readonly annotationViewService: AnnotationViewService
  ) {
    super();
    this.fullScreen$.pipe(untilDestroyed(this)).subscribe(fs => {
      if (!fs) {
        this.reset();
      } else {
        this.changeFitSizeState(1);
      }
    });

    this.projectLoaderService.projectUserPermissions
      .pipe(untilDestroyed(this))
      .subscribe(userPerms => {
        if (userPerms.loadState.status === LoadStatusEnum.Loaded) {
          this.currentUser = userPerms.data.User;
        }
      });
  }

  ngOnDestroy(): void {
    this.closing$.next(false);
    this.startInAnnoEditMode = false;
    if (this.selectedAnnotationsSubscription) {
      this.selectedAnnotationsSubscription.unsubscribe();
    }
  }

  async initFullScreen() {
    this.customLoadingSubject.next(true);
    setTimeout(() => (this.fullScreen = true), 0);
  }

  async showInFullScreen(
    options: {
      currentFault?: ViewFault;
      currentItem?: Attachment;
      items?: Array<Attachment>;
      startInAnnoEditMode?: boolean;
      itemLoader?: (item: Attachment) => Promise<ViewerItem>;
    },
    doInit: boolean = true
  ) {
    this.closing$.next(false);
    if (!this.isImage(options?.currentItem)) {
      await this.attachmentService.downloadAttachment(options?.currentItem);
      return;
    }
    this.itemLoader = options.itemLoader;
    this.currentFault = options.currentFault;
    this.currentItem = options.currentItem;
    this.items = (options.items ? options.items : [options.currentItem]).filter(a => this.isImage(a));

    this.startInAnnoEditMode = options?.startInAnnoEditMode;

    if (doInit) {
      this.fullScreen = true;
    }
  }

  private isImage(a: Attachment): boolean {
    if (!a?.data?.MimeType) {
      return false;
    }
    if (a.data.MimeType.startsWith('image')) {
      return true;
    }
    if (a.data.Type === AttachmentType.Picture || a.data.Type === AttachmentType.PlanSection) {
      return true;
    }
    return false;
  }

  private reset() {
    this.currentItem = null;
    this.customLoadingSubject.next(false);
  }


  private waitForFullscreenAndAnno() {
    return firstValueFrom(
      combineLatest([this.fullScreen$.pipe(filter(Boolean)), this.annoLayerReady$.pipe(filter(Boolean))]).pipe(take(1))
    );
  }

  private async updateAnnotationPermissions(): Promise<void> {
    const currentAttachment = this.currentItem as Attachment;

    if (!currentAttachment || !this.currentUser) {
      this.imageViewerOptions = {
        ...this.imageViewerOptions,
        annotations: {
          ...this.imageViewerOptions.annotations,
          allowEdit: false
        }
      };
      return;
    }

    const canStart = await firstValueFrom(
      this.annotationPermissionsService.canStartAnnotationMode()
    );

    this.imageViewerOptions = {
      ...this.imageViewerOptions,
      annotations: {
        ...this.imageViewerOptions.annotations,
        allowEdit: canStart
      }
    };
  }

}

export function sortAttachments(attachments: Attachment[]) {
  return attachments.sort((a, b) => {
    const d2 = (b.data?.CreatedOn ? new Date(b.data?.CreatedOn) : new Date('9999-12-31T23:59:59'));
    const d1 = (a.data?.CreatedOn ? new Date(a.data?.CreatedOn) : new Date('9999-12-31T23:59:59'));
    return d2.getTime() - d1.getTime();
  });
}
